import { Component, Inject, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Destroyable, SaveForm, Toaster, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { Connection } from '@tv3/store/connection/connection.model'
import {
  AirbnbChannelService,
  AirbnbUpdateBookingSettingsPayload,
  airbnbCheckinEndTimeOptions,
  airbnbCheckinStartTimeOptions,
  airbnbCheckoutTimeOptions,
  airbnbDiscountValidator,
  airbnbErrorDisplayMessage,
  airbnbFixCheckinTime,
  airbnbFromDiscountToPriceFactor,
  airbnbFromPriceFactorToDiscount,
  airbnbInstantBookingAllowedCategories,
  airbnbListingExpectationTypes,
  airbnbLongTermCancellationPolicies,
  airbnbSelectableCancellationPolicies,
  airbnbSuperStrictPolicies,
  turnOffInstantBookingWarning,
} from '@tokeet-frontend/channels'
import * as lodash from 'lodash'
import { AirbnbHelperService } from '../../airbnb-helper.service'
import { combineLatest } from 'rxjs'

@Component({
  selector: 'app-airbnb-booking-settings-dialog',
  templateUrl: './airbnb-booking-settings-dialog.component.html',
  styleUrls: ['./airbnb-booking-settings-dialog.component.scss'],
})
export class AirbnbBookingSettingsDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    cancellation_policy_settings: this.fb.group({
      lts_cancellation_policy_id: [, [Validators.required]], //  "CANCEL_LONG_TERM_FAIR",
      cancellation_policy_category: [, [Validators.required]], //  "super_strict_60",
      non_refundable_price_factor: [, [airbnbDiscountValidator()]], //  (0, 1)
    }),
    guest_controls: this.fb.group({
      allows_smoking_as_host: [],
      allows_pets_as_host: [],
      allows_events_as_host: [],
    }),
    check_in_time_start: [], //  "8", "9", "10", ..., "25"or "FLEXIBLE". "NOT_SELECTED"
    check_in_time_end: [], //  "9", "10", "11", ..., "26"or "FLEXIBLE". "NOT_SELECTED"
    check_out_time: [], //  0-23,
    instant_booking_allowed_category: [], // "well_reviewed_guests",
    instant_book_welcome_message: [], // "hello"
    listing_expectations_for_guests: this.fb.array([]), // "hello"
  })
  get cancellationPolicy() {
    return this.form.get('cancellation_policy_settings') as FormGroup
  }
  get isDiscountIneligible() {
    return this.discountIneligiblePolicies.includes(this.cancellationPolicy.get('cancellation_policy_category').value)
  }
  get guestControls() {
    return this.form.get('guest_controls') as FormGroup
  }
  get expectations() {
    return this.form.get('listing_expectations_for_guests') as FormArray
  }
  // refined per listing once GET booking settings returns `eligible_cancellation_policies`
  cancellationPolicies = airbnbSelectableCancellationPolicies()
  longTermCancellationPolicies = airbnbLongTermCancellationPolicies
  // Airbnb rejects the non-refundable discount silently (HTTP 200, field dropped) for these policies
  discountIneligiblePolicies = airbnbSuperStrictPolicies
  airbnbCheckinStartTimeOptions = airbnbCheckinStartTimeOptions
  airbnbCheckinEndTimeOptions = airbnbCheckinEndTimeOptions
  airbnbCheckoutTimeOptions = airbnbCheckoutTimeOptions
  airbnbInstantBookingAllowedCategories = airbnbInstantBookingAllowedCategories
  airbnbListingExpectationTypes = airbnbListingExpectationTypes
  turnOffInstantBookingWarning = turnOffInstantBookingWarning

  isFullSync = false

  constructor(
    public dialogRef: MatDialogRef<AirbnbBookingSettingsDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private airbnbChannelService: AirbnbChannelService,
    private airbnbHelperService: AirbnbHelperService,
    @Inject(MAT_DIALOG_DATA) public data: { connection: Connection }
  ) {
    super()
  }

  ngOnInit() {
    this.cancellationPolicy
      .get('cancellation_policy_category')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe(() => this.onCancellationPolicyChanged())

    const conn = this.data.connection
    combineLatest([
      this.airbnbChannelService.getListingSyncCategory(conn.propertyId, conn.roomId),
      this.airbnbChannelService.getBookingSettingsV2(conn.channelId, conn.propertyId, conn.roomId),
    ]).subscribe(
      ([sync, settings]) => {
        this.isFullSync = sync.synchronization_category == 'sync_all'
        this.cancellationPolicies = airbnbSelectableCancellationPolicies(settings.cancellation_policy_settings)
        this.form.patchValue({
          ...settings,
          check_in_time_start: airbnbFixCheckinTime(settings.check_in_time_start),
          check_in_time_end: airbnbFixCheckinTime(settings.check_in_time_end),
          cancellation_policy_settings: {
            ...settings.cancellation_policy_settings,
            non_refundable_price_factor: airbnbFromPriceFactorToDiscount(
              settings.cancellation_policy_settings.non_refundable_price_factor
            ),
          },
        })
        if (!!settings.listing_expectations_for_guests?.length) {
          settings.listing_expectations_for_guests.forEach((expectation) => this.onAddExpectation(expectation))
        }
      },
      (error) => {
        this.toaster.error(
          airbnbErrorDisplayMessage(this.toaster.getError(error)),
          $localize`Unable to load booking settings`
        )
      }
    )
  }

  close() {
    this.dialogRef.close()
  }

  onAddExpectation(data?: { type: string; added_details: string }) {
    this.expectations.push(
      this.fb.group({
        type: [data?.type, [Validators.required]],
        added_details: [data?.added_details],
      })
    )
  }

  onRemoveExpectation(index: number) {
    this.expectations.removeAt(index)
  }

  onCancellationPolicyChanged() {
    const ctrl = this.cancellationPolicy.get('non_refundable_price_factor')
    if (this.isDiscountIneligible) {
      // sending null removes the discount on Airbnb's side, matching what Airbnb enforces anyway
      ctrl.setValue(null)
      ctrl.disable()
    } else {
      ctrl.enable()
    }
  }

  onUpdateSyncCategoryAirBnBV2() {
    this.airbnbHelperService.setSyncCategoryV2(this.data.connection).subscribe((sync) => {
      this.isFullSync = sync.synchronization_category === 'sync_all'
    })
  }

  @SaveForm()
  save(form: FormGroup) {
    const data = this.form.getRawValue() as AirbnbUpdateBookingSettingsPayload
    data.cancellation_policy_settings.non_refundable_price_factor = airbnbFromDiscountToPriceFactor(
      data.cancellation_policy_settings.non_refundable_price_factor
    )
    this.airbnbChannelService
      .updateBookingSettingsV2({
        ...data,
        // @ts-ignore
        guest_controls: lodash.mapValues(data.guest_controls, (v, k) => (lodash.startsWith(k, 'allows_') ? !!v : v)),
        channelId: this.data.connection.channelId,
        propertyId: this.data.connection.propertyId,
        roomid: this.data.connection.roomId,
      })
      .subscribe(
        () => {
          this.toaster.success($localize`Booking settings saved successfully`)
        },
        (error) => {
          this.toaster.error(
            airbnbErrorDisplayMessage(this.toaster.getError(error)),
            $localize`Unable to save booking settings`
          )
        }
      )
  }
}
